import { useGetErrorMessage } from '@/app/(dashboard)/hooks/useGetErrorMessage'
import { Step1 } from '@/app/(dashboard)/upload-version/step1/Step1'
import { Step2 } from '@/app/(dashboard)/upload-version/step2/Step2'
import { Step3 } from '@/app/(dashboard)/upload-version/step3/Step3'
import {
  UploadVersionProvider,
  useUploadVersion,
} from '@/app/(dashboard)/upload-version/UploadVersionProvider'
import { useComparisonCreation } from '@/app/crunches/[id]/hooks/useComparisonCreation'
import { BaseDialog } from '@/dc-shared-web/src'
import { DocumentSearchResult } from '@/dc-shared-web/src/hooks/api/document/types'
import { UploadDocumentParams } from '@/dc-shared-web/src/hooks/api/document/uploadDocument'
import { useGetDocument } from '@/dc-shared-web/src/hooks/api/document/useGetDocument'
import { useGetDocumentById } from '@/dc-shared-web/src/hooks/api/document/useGetDocumentById'
import { useUploadDocument } from '@/dc-shared-web/src/hooks/api/document/useUploadDocument'
import {
  DOCUMENTS_QUERY_KEY,
  LIST_QUERY_KEY,
} from '@/dc-shared-web/src/hooks/api/queryKeys'
import { useQueryClient } from 'react-query'
import { FC } from 'react'
import { noop } from 'lodash'

interface UploadVersionModalProps {
  onClose: () => void
  document: DocumentSearchResult
}

export const UploadVersionModal: FC<UploadVersionModalProps> = ({
  onClose,
  document,
}) => {
  return (
    <UploadVersionProvider document={document}>
      <UploadVersionModalContent onClose={onClose} />
    </UploadVersionProvider>
  )
}

const UploadVersionModalContent: FC<
  Pick<UploadVersionModalProps, 'onClose'>
> = ({ onClose }) => {
  const { currentStep, document } = useUploadVersion()
  const queryClient = useQueryClient()

  const {
    mutateAsync: uploadDocument,
    isLoading: isUploading,
    error,
  } = useUploadDocument()

  const { data: crunchDataSvcCrunches } = useGetDocument(document.uuid)
  const { data: crunchData } = useGetDocumentById(document.uuid)

  const { handleComparison } = useComparisonCreation({
    onProcessingComparisonAdded: noop,
    onSuccess: noop,
  })

  const errorMessage = useGetErrorMessage(error)

  const handleUpload = (uploadParams: UploadDocumentParams): Promise<void> => {
    return new Promise((resolve, reject) => {
      uploadDocument(uploadParams, {
        onSuccess: async (result) => {
          try {
            if (!crunchData) {
              reject(new Error('Error getting crunch data to compare.'))
              return
            }

            await handleComparison(
              result.crunch,
              crunchData,
              uploadParams.file.name
            )

            await queryClient.invalidateQueries([
              DOCUMENTS_QUERY_KEY,
              LIST_QUERY_KEY,
            ])

            resolve()
          } catch (e) {
            reject(e)
          }
        },
        onError: reject,
      })
    })
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <BaseDialog
      isOpen
      showCloseButton={!isUploading}
      onClose={handleClose}
      title='Version comparisons'
      size='large'
      disableOutsideClick
      disableEscapeClose
      /* Actions are added by each step component. */
      primaryActionSlot={<></>}
      secondaryActionSlot={<></>}
    >
      {currentStep === 1 && <Step1 onCancel={onClose} />}
      {currentStep === 2 && (
        <Step2
          onUpload={handleUpload}
          errorMessage={errorMessage}
          solutionTypeUuid={crunchDataSvcCrunches?.tocData?.origin_uuid}
        />
      )}
      {currentStep === 3 && <Step3 onClose={onClose} />}
    </BaseDialog>
  )
}
